
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAdminCalendar } from "@/hooks/calendar/useAdminCalendar";
import { Header } from "@/components/Header";
import { DashboardContent } from "@/components/admin/dashboard/DashboardContent";
import { DashboardDialogs } from "@/components/admin/dashboard/DashboardDialogs";
import { LoadingScreen } from "@/components/admin/dashboard/LoadingScreen";
import { useVendorService } from "@/hooks/useVendorService";
import { useAdminCheckQuery } from "@/hooks/useAdminCheckQuery";
import { useServicePermission } from "@/hooks/useServicePermission";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { BookingCompletionData } from "@/components/booking/BookingCompletionDialog";
import { BookingMetricsCards } from "@/components/admin/dashboard/BookingMetricsCards";
import { VendorSidebar } from "@/components/admin/dashboard/VendorSidebar";
import VendorSettings from "./VendorSettings";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useServiceEditAccess } from "@/hooks/useServiceEditAccess";
import { Calendar, BookOpen, Star, Settings, HelpCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { parseStoredDateTime } from "@/utils/timeUtils";

const AdminCalendarView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { loading: serviceLoading, serviceName, serviceData } = useVendorService(id);
  const [activeView, setActiveView] = useState("calendar");
  const [calendarView, setCalendarView] = useState("timeGridDay");
  const [timeFilter, setTimeFilter] = useState("upcoming");
  const { data: isAdmin, isLoading: isAdminLoading } = useAdminCheckQuery();
  const { canEdit, isOwner } = useServicePermission(id || "");
  const { showEditInSidebar } = useServiceEditAccess(id || "");
  const isMobile = useIsMobile();

  // Service menu items for mobile navigation
  const serviceMenuItems = [
    {
      view: "calendar",
      label: "Calendar",
      icon: Calendar,
      visible: true
    },
    {
      view: "bookings",
      label: "Bookings",
      icon: BookOpen,
      visible: true
    },
    {
      view: "reviews",
      label: "Reviews",
      icon: Star,
      visible: true
    },
    {
      view: "settings",
      label: "Settings",
      icon: Settings,
      visible: true
    },
    {
      view: "help",
      label: "Help",
      icon: HelpCircle,
      visible: true
    }
  ];

  // Check if URL contains a specific view and set it
  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('/bookings')) {
      setActiveView('bookings');
    } else if (path.includes('/reviews')) {
      setActiveView('reviews');
    } else if (path.includes('/settings')) {
      setActiveView('settings');
    }
  }, [window.location.pathname]);

  useEffect(() => {
    window.serviceDashboardState = {
      activeView,
      setActiveView,
    };
  }, [activeView]);

  const {
    selectedDate,
    setSelectedDate,
    bookings,
    selectedTimeSlot,
    setSelectedTimeSlot,
    isBlockingDialogOpen,
    setIsBlockingDialogOpen,
    isModifyDialogOpen,
    setIsModifyDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isTravelCancelDialogOpen,
    setIsTravelCancelDialogOpen,
    loading,
    selectedBlockedBooking,
    setSelectedBlockedBooking,
    selectedTravelBlock,
    setSelectedTravelBlock,
    selectedBooking,
    setSelectedBooking,
    isBookingDetailsOpen,
    setIsBookingDetailsOpen,
    handleTimeSlotSelect,
    handleDeleteBlock,
    handleModifyBlock,
    handleBlockTime,
    handleBookingAction,
    handleDeleteFromDialog,
    handleCancelTravelBlock,
    handleCompleteBooking,
    isAuthorized,
    actionInProgress,
    progressValue,
    showSuccessAnimation,
    isOwner: calendarIsOwner,
    canManageBookings
  } = useAdminCalendar(id);

  // console.log("ServiceDashboard permissions:", { isAuthorized, isOwner, canManageBookings });
  console.log("🔥 Vendor clicked - Selected booking:", selectedBooking);

  const handleCompleteBookingWrapper = async (bookingId: number, completionData: BookingCompletionData): Promise<void> => {
    if (handleCompleteBooking) {
      const success = await handleCompleteBooking(bookingId, completionData);
      if (success && isBookingDetailsOpen) {
        // Close the booking dialog if completion was successful
        setTimeout(() => {
          setIsBookingDetailsOpen(false);
          setSelectedBooking(null);
        }, 2000); // Give time for the success animation to complete
      }
    } else {
      toast({
        title: "Operation not supported",
        description: "The booking completion functionality is not available.",
        variant: "destructive"
      });
    }
  };

  if (serviceLoading || !id) {
    return <LoadingScreen />;
  }

  const handleDateSelect = (date: Date | undefined) => {
    if (!canManageBookings) {
      console.log("User not authorized to select dates");
      return;
    }
    
    if (date) {
      setSelectedDate(date);
      
      const exactTimeStr = format(date, 'HH:mm');
      const matchingBooking = bookings.find(booking => {
        const bookingStart = new Date(booking.start_time);
        return format(bookingStart, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd') && 
               format(bookingStart, 'HH:mm') === exactTimeStr;
      });
      
      if (matchingBooking) {
        if (matchingBooking.status === 'blocked') {
          setSelectedBlockedBooking(matchingBooking);
          setIsModifyDialogOpen(true);
        } else if (matchingBooking.status === 'travel') {
          setSelectedTravelBlock(matchingBooking);
          setIsTravelCancelDialogOpen(true);
        } else {
          setSelectedBooking(matchingBooking);
          setIsBookingDetailsOpen(true);
        }
      } else if (exactTimeStr && exactTimeStr !== '00:00') {
        setSelectedTimeSlot(exactTimeStr);
        setIsBlockingDialogOpen(true);
      }
    }
  };

  const handleModifyOrViewBooking = (booking: any) => {
    if (booking.status === 'blocked') {
      setSelectedBlockedBooking(booking);
      setIsModifyDialogOpen(true);
    } else if (booking.status === 'travel') {
      setSelectedTravelBlock(booking);
      setIsTravelCancelDialogOpen(true);
    } else {
      setSelectedBooking(booking);
      setIsBookingDetailsOpen(true);
    }
  };

  const handleDeleteBlockRequest = (booking: any) => {
    if (booking.status === 'travel') {
      setSelectedTravelBlock(booking);
      setIsTravelCancelDialogOpen(true);
    } else {
      setSelectedBlockedBooking(booking);
      setIsDeleteDialogOpen(true);
    }
  };

  const handleAcceptBookingRequest = async (bookingId: number): Promise<void> => {
    await handleBookingAction(bookingId, 'accepted');
  };

  const handleDeclineBookingRequest = async (bookingId: number, reason?: string): Promise<void> => {
    await handleBookingAction(bookingId, 'declined', reason);
  };

  const handleBlockTimeWrapper = async (startTime: string, endTime: string): Promise<void> => {
    await handleBlockTime(startTime, endTime);
  };

  const handleModifyBlockWrapper = async (startTime: string, endTime: string): Promise<void> => {
    await handleModifyBlock(startTime, endTime);
  };

  const handleDeleteBlockWrapper = async (): Promise<void> => {
    await handleDeleteBlock();
  };

  const handleCancelTravelBlockWrapper = async (): Promise<void> => {
    await handleCancelTravelBlock();
  };

  const handleBookingActionWrapper = async (bookingId: number, status: 'accepted' | 'declined', reason?: string): Promise<void> => {
    await handleBookingAction(bookingId, status, reason);
  };

  const handleCalendarViewChange = (view: string) => {
    setCalendarView(view);
  };

  const handleViewChange = (view: string) => {
    if (view === "help") {
      navigate(`/vendor/help?serviceId=${id}`);
    } else {
      setActiveView(view);
    }
  };

  // Filter bookings based on time (upcoming/past)
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  
  let timeFilteredBookings = bookings;
  
  if (timeFilter === "upcoming") {
    timeFilteredBookings = bookings.filter(booking => {
      const bookingDate = parseStoredDateTime(booking.start_time);
      return bookingDate >= now;
    });
  } else if (timeFilter === "past") {
    timeFilteredBookings = bookings.filter(booking => {
      const bookingDate = parseStoredDateTime(booking.start_time);
      return bookingDate < now;
    });
  }

  // Calculate booking counts by status from filtered bookings
  const bookingCounts = {
    new: timeFilteredBookings.filter(b => b.status === 'new').length,
    accepted: timeFilteredBookings.filter(b => b.status === 'accepted').length,
    cancelled: timeFilteredBookings.filter(b => b.status === 'cancelled').length,
    declined: timeFilteredBookings.filter(b => b.status === 'declined').length,
    completed: timeFilteredBookings.filter(b => b.status === 'completed').length,
  };

  const renderContent = () => {
    if (activeView === "settings") {
      return <VendorSettings />;
    }

    return (
      <>
        {/* Display time filter and metrics cards if we're on the bookings view or calendar view */}
        {(activeView === "bookings" || activeView === "calendar") && (
          <div className="mb-4">
            <Tabs value={timeFilter} onValueChange={setTimeFilter} className="w-full mb-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="upcoming">Upcoming Bookings</TabsTrigger>
                <TabsTrigger value="past">Past Bookings</TabsTrigger>
              </TabsList>
            </Tabs>
            
            <BookingMetricsCards 
              serviceId={id} 
              bookingCounts={bookingCounts}
              isServiceDashboard={true} 
              timeFilter={timeFilter}
            />
          </div>
        )}
        
        <DashboardContent 
          activeView={activeView}
          serviceId={id || ""}
          selectedDate={selectedDate}
          bookings={timeFilteredBookings}
          serviceName={serviceName}
          service={serviceData}
          onDateSelect={handleDateSelect}
          onModifyBlock={handleModifyOrViewBooking}
          onDeleteBlock={handleDeleteBlockRequest}
          onViewBooking={(booking) => {
            setSelectedBooking(booking);
            setIsBookingDetailsOpen(true);
          }}
          onAcceptBooking={handleAcceptBookingRequest}
          onDeclineBooking={handleDeclineBookingRequest}
          calendarView={calendarView}
          onCalendarViewChange={handleCalendarViewChange}
          onBookingSelect={(booking) => {
            setSelectedBooking(booking);
            setIsBookingDetailsOpen(true);
          }}
          onTimeSlotSelect={handleTimeSlotSelect}
        />
      </>
    );
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50 flex flex-col w-full">
        <Header 
          isServiceDashboard={true}
          serviceMenuItems={serviceMenuItems}
          onServiceViewChange={handleViewChange}
          activeServiceView={activeView}
        />
        
        <div className="flex flex-1 mt-16 w-full">
          {!isMobile && (
            <VendorSidebar
              activeView={activeView}
              serviceId={id || ""}
              onViewChange={handleViewChange}
            />
          )}
          
          <div className="flex-1 p-4 md:p-6">
            <div className="mb-6 flex items-center gap-4">
              {!isMobile && <SidebarTrigger className="md:hidden" />}
              <h2 className="text-2xl font-bold text-gray-900">{serviceName}</h2>
            </div>
            {renderContent()}
          </div>
        </div>
      </div>

      <DashboardDialogs
        serviceName={serviceName}
        selectedDate={selectedDate}
        selectedTimeSlot={selectedTimeSlot}
        isBlockingDialogOpen={isBlockingDialogOpen}
        isModifyDialogOpen={isModifyDialogOpen}
        isDeleteDialogOpen={isDeleteDialogOpen}
        isTravelCancelDialogOpen={isTravelCancelDialogOpen}
        isBookingDetailsOpen={isBookingDetailsOpen}
        selectedBlockedBooking={selectedBlockedBooking}
        selectedTravelBlock={selectedTravelBlock}
        selectedBooking={selectedBooking}
        loading={loading}
        serviceId={id || ""}
        onCloseBlockingDialog={() => {
          setIsBlockingDialogOpen(false);
          setSelectedTimeSlot("");
        }}
        onCloseModifyDialog={() => {
          setIsModifyDialogOpen(false);
          setSelectedBlockedBooking(null);
        }}
        onCloseDeleteDialog={setIsDeleteDialogOpen}
        onCloseTravelCancelDialog={() => {
          setIsTravelCancelDialogOpen(false);
          setSelectedTravelBlock(null);
        }}
        onCloseBookingDetailsDialog={() => {
          setIsBookingDetailsOpen(false);
          setSelectedBooking(null);
        }}
        onBlockTime={handleBlockTimeWrapper}
        onModifyBlock={handleModifyBlockWrapper}
        onDeleteBlock={handleDeleteBlockWrapper}
        onDeleteFromDialog={handleDeleteFromDialog}
        onCancelTravelBlock={handleCancelTravelBlockWrapper}
        onAcceptBooking={async (bookingId) => {
          await handleBookingActionWrapper(bookingId, 'accepted');
          // Close dialog after successful acceptance
          setTimeout(() => {
            if (isBookingDetailsOpen) {
              setIsBookingDetailsOpen(false);
              setSelectedBooking(null);
            }
          }, 2000);
        }}
        onDeclineBooking={async (bookingId, reason) => {
          await handleBookingActionWrapper(bookingId, 'declined', reason);
          // Close dialog after successful decline
          setTimeout(() => {
            if (isBookingDetailsOpen) {
              setIsBookingDetailsOpen(false);
              setSelectedBooking(null);
            }
          }, 2000);
        }}
        onCompleteBooking={handleCompleteBookingWrapper}
        actionInProgress={actionInProgress}
        progressValue={progressValue}
        showSuccessAnimation={showSuccessAnimation}
      />
    </SidebarProvider>
  );
};

export default AdminCalendarView;
